import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import Table from '../../../../component/table/Table';
import OdKpiRow from '../components/OdKpiRow';
import odTableStyles from '../components/odTableStyles';
import OdCoordCell from '../components/OdCoordCell';
import OdSelect from '../components/OdSelect';
import th from '../components/odColumnHeader';
import { buildTripNumbers, compactDuration } from '../odAnalyticsUtils';

const num = (value) => <span className="od-cell-num">{value}</span>;

const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/** '—' rather than '0s' when the figure is absent: no rollup is not zero idling. */
const dur = (seconds) => (Number.isFinite(seconds) ? compactDuration(seconds) : '—');

const SCOPE_LABELS = { 'in-trip': 'In trip', 'outside-trips': 'Outside trips' };

/** Badge hue per known class code; an unrecognised code falls back to the plain number. */
const CLASS_CODES = new Set([400, 450, 460]);
const classBadge = (code) => (CLASS_CODES.has(code)
    ? <span className={`od-badge od-badge-c${code}`}>{code}</span>
    : num(dash(code)));

/**
 * Discrete idling episodes with classification.
 *
 * Rows come from GET /v3/trip/analytics (`data.idle`). An episode is a coalesced run of
 * stationary, ignition-on samples; the server classifies each one and says why in `reason`,
 * which is the column a fleet manager quotes back to a driver.
 *
 * Scope is the reason this view is window-scoped rather than per-trip: idling outside any
 * trip has no trip to hang off, and it is a headline number.
 */
export const idleColumns = ({ showAsset, tripNumbers, zone }) => [
    showAsset && {
        id: 'asset',
        name: th('Asset'),
        selector: (row) => row.assetName,
        sortable: true,
        minWidth: '8rem',
        cell: (row) => (
            <div className="od-cell-stack">
                <span className="od-cell-strong">{row.assetName}</span>
                <span className="od-cell-muted">{row.deviceSerial || '—'}</span>
            </div>
        ),
    },
    {
        id: 'start',
        name: th('Start', 'When the episode began, in the agency timezone.'),
        selector: (row) => row.startTime,
        sortable: true,
        // Wide enough for the whole stamp on one line: at 9rem the DD-MM-YYYY date wrapped under
        // its own clock, because a column here is sized by its minWidth rather than its content.
        minWidth: '11rem',
        cell: (row) => (row.startTime ? moment.tz(row.startTime, zone).format('DD-MM-YYYY HH:mm:ss') : '—'),
    },
    {
        id: 'duration',
        name: th('Duration', 'How long the vehicle idled — stationary with the engine on.'),
        selector: (row) => row.durationSec,
        sortable: true,
        right: true,
        minWidth: '5.5rem',
        cell: (row) => num(dur(row.durationSec)),
    },
    {
        id: 'scope',
        name: th('Scope', 'Whether the episode fell inside a trip or between trips.'),
        selector: (row) => row.scope,
        sortable: true,
        minWidth: '6.5rem',
        // An out-of-trip episode has no tripId, which is exactly why it needs its own label.
        cell: (row) => (
            <div className="od-cell-stack">
                <span>{SCOPE_LABELS[row.scope] || dash(row.scope)}</span>
                {tripNumbers.get(row.tripId) !== undefined
                    && <span className="od-cell-muted">{`Trip #${tripNumbers.get(row.tripId)}`}</span>}
            </div>
        ),
    },
    {
        id: 'classCode',
        name: th('Class', 'The state the server classified this episode as: 400 idle, 450 excessive, 460 excusable.'),
        selector: (row) => row.classCode,
        sortable: true,
        right: true,
        minWidth: '4.5rem',
        cell: (row) => classBadge(row.classCode),
    },
    {
        id: 'excess',
        name: th('Over limit', 'Idle time beyond the agency\'s excessive-idle limit.'),
        selector: (row) => row.excessSec,
        sortable: true,
        right: true,
        minWidth: '5.75rem',
        cell: (row) => num(dur(row.excessSec)),
    },
    {
        id: 'excusable',
        name: th('Excused', 'Idle time excused by policy — hot or cold ambient temperature, for example.'),
        selector: (row) => row.excusableSec,
        sortable: true,
        right: true,
        minWidth: '5.25rem',
        cell: (row) => num(dur(row.excusableSec)),
    },
    {
        id: 'billable',
        name: th('Billable', 'Idle time left once the excused portion is removed.'),
        selector: (row) => row.billableSec,
        sortable: true,
        right: true,
        minWidth: '5.25rem',
        cell: (row) => num(dur(row.billableSec)),
    },
    {
        id: 'basis',
        name: th('Basis', 'Why the server classified the episode this way — the sentence to quote back to a driver.'),
        selector: (row) => row.reason,
        grow: 2,
        minWidth: '12rem',
        cell: (row) => dash(row.reason),
    },
    {
        id: 'location',
        name: th('Location', 'Where the episode was recorded. Click the row to see the point on the Route Map, the coordinates to open Google Maps, or ⧉ to copy them.'),
        // Sorted by latitude — see the same column on Safety. The address stays on hover.
        selector: (row) => row.lat,
        sortable: true,
        grow: 2,
        minWidth: '13rem',
        cell: (row) => <OdCoordCell lat={row.lat} lon={row.lon} address={row.location} />,
    },
].filter(Boolean);

/** fleetconsole's `idleScopeSel` and `idleClassSel`. `''` is no filter on either. */
const SCOPE_OPTIONS = [
    { value: '', label: 'All scopes' },
    { value: 'in-trip', label: SCOPE_LABELS['in-trip'] },
    { value: 'outside-trips', label: SCOPE_LABELS['outside-trips'] },
];

const CLASS_OPTIONS = [
    { value: '', label: 'All classes' },
    { value: '400', label: '400 idle' },
    { value: '450', label: '450 excessive' },
    { value: '460', label: '460 excusable' },
    // One option for both codes: an EV standing on its traction battery and a hybrid doing the
    // same are the same question, and neither is idling in the sense the limits are about.
    { value: 'standby', label: '800/850 standby' },
];

const STANDBY_CODES = new Set([800, 850]);

const IdleView = (props) => {
    const {
        episodes, policy, policyByAsset, rollup, availability, trips, timezone, error, tooLarge,
        onFocusEpisode,
    } = props;

    const zone = timezone || 'UTC';
    const [scopeFilter, setScopeFilter] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const rows = useMemo(() => (Array.isArray(episodes) ? episodes : []), [episodes]);
    // A lens on the table only: the tiles come off the server rollup, which reconciles against
    // total stationary time and cannot be narrowed without lying about that invariant.
    const visible = useMemo(() => rows.filter((row) => {
        if (scopeFilter && row.scope !== scopeFilter) return false;
        if (classFilter === 'standby') return STANDBY_CODES.has(Number(row.classCode));
        if (classFilter && String(row.classCode) !== classFilter) return false;
        return true;
    }), [rows, scopeFilter, classFilter]);
    const tripNumbers = useMemo(() => buildTripNumbers(trips), [trips]);
    const showAsset = useMemo(() => new Set(rows.map((row) => row.assetName)).size > 1, [rows]);

    const columns = useMemo(
        () => idleColumns({ showAsset, tripNumbers, zone }),
        [showAsset, tripNumbers, zone],
    );

    // Straight off the server rollup — the client never re-derives these, because
    // `sec + fragmentSec` reconciling against total stationary time is the server's
    // invariant and summing visible rows would quietly drop the sub-minimum fragments.
    const tiles = [
        { key: 'episodes', label: 'Idle episodes', value: rows.length },
        { key: 'total', label: 'Total idle', value: dur(rollup?.sec) },
        { key: 'inTrip', label: 'During trips', value: dur(rollup?.inTripSec) },
        { key: 'outTrip', label: 'Outside trips', value: dur(rollup?.outTripSec) },
        { key: 'excess', label: 'Over the limit', value: dur(rollup?.excessSec) },
        {
            key: 'billable',
            label: 'Billable waste',
            value: dur(rollup?.billableSec),
            sub: Number.isFinite(rollup?.excusableSec) ? `${dur(rollup.excusableSec)} excused` : undefined,
            tone: rollup?.billableSec > 0 ? 'bad' : undefined,
        },
    ];

    const emptyState = () => {
        if (error) {
            return {
                title: tooLarge ? 'Window too large' : 'Unable to load idling episodes',
                text: error,
            };
        }
        if (availability === 'available') {
            return {
                title: 'No idling episodes in this window',
                text: 'Telemetry covered this window and every stop was too short to count as idling.',
            };
        }
        return {
            title: 'No idling signal in this window',
            text: 'No ignition or speed telemetry came back for the selected period, so idling cannot'
                + ' be measured. A zero here would mean the signal is missing — not that nothing idled.',
        };
    };

    const empty = rows.length === 0 ? emptyState() : null;
    const filtered = visible.length !== rows.length;

    const policyNote = policy && Number.isFinite(policy.excessiveIdleLimitSec)
        ? `Idling past ${compactDuration(policy.excessiveIdleLimitSec)} counts as excessive; stops under `
            + `${dur(policy.minIdleSec)} are not counted at all. Extreme ambient temperature excuses the excess.`
        : 'Excess is measured against the agency idling limit. Extreme ambient temperature excuses it.';

    // `policy` goes silent on a mixed fleet — one sentence cannot say "past 5m is excessive" when
    // it is 10m on the trucks. Listing each asset's own limit is the honest version of that
    // sentence, so the reader can trace a number to the preset that produced it. Only worth showing
    // when the assets actually differ; when they agree, `policyNote` already said it.
    const perAssetLimits = useMemo(() => {
        const entries = Object.entries(policyByAsset || {})
            .filter(([, value]) => Number.isFinite(value?.excessiveIdleLimitSec));
        if (entries.length < 2) return [];

        const distinct = new Set(entries.map(([, value]) => value.excessiveIdleLimitSec));
        if (distinct.size < 2) return [];

        return entries
            .map(([assetName, value]) => ({
                assetName,
                limit: compactDuration(value.excessiveIdleLimitSec),
            }))
            .sort((left, right) => left.assetName.localeCompare(right.assetName));
    }, [policyByAsset]);

    return (
        <div className="od-view">
            <OdKpiRow tiles={tiles} />

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">
                            Idling episodes
                            <span className="od-badge od-badge-neutral">
                                {filtered ? `${visible.length} of ${rows.length}` : `${rows.length}`}
                            </span>
                        </div>
                        <div className="od-card-subtitle">
                            Individual stops with duration, classification and location
                        </div>
                    </div>
                    <div className="od-card-actions">
                        <OdSelect
                          label="Scope"
                          value={scopeFilter}
                          onChange={setScopeFilter}
                          options={SCOPE_OPTIONS}
                          ariaLabel="Filter by scope"
                        />
                        <OdSelect
                          label="Class"
                          value={classFilter}
                          onChange={setClassFilter}
                          options={CLASS_OPTIONS}
                          ariaLabel="Filter by class"
                        />
                    </div>
                </div>

                {empty ? (
                    <div className="od-empty">
                        <div className="od-empty-title">{empty.title}</div>
                        <div className="od-empty-text">{empty.text}</div>
                    </div>
                ) : (
                    <div className="od-card-body od-table-wrap">
                        <Table
                          tableData={visible}
                          columns={columns}
                          module="odAnalytics"
                          // Same gesture as Safety: the row pins the point, the ◉ button is the
                          // precise version of it.
                          onRowClicked={onFocusEpisode}
                          highlightOnHover
                          pointerOnHover
                          pagination={visible.length > 10}
                          paginationServer={false}
                          paginationPerPage={10}
                          handlePageChange={() => {}}
                          handlePerRowsChange={() => {}}
                          setSelectedRowCount={() => {}}
                          totalRows={visible.length}
                          customStyles={odTableStyles}
                          backgroundColor="#F9FAFB"
                        />
                    </div>
                )}

                <div className="od-card-note">
                    {policyNote}
                    {perAssetLimits.length > 0 && (
                        <>
                            {' Per asset: '}
                            {perAssetLimits
                                .map(({ assetName, limit }) => `${assetName} ${limit}`)
                                .join(', ')}
                            .
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IdleView;
